import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, CheckCircle, Clock, XCircle, Receipt, ArrowRight, Home } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { bookingsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const fmt = (n) => n?.toLocaleString('en-IN') || '0';

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
  pending:   { label: 'Pending',   color: 'bg-amber-100 text-amber-700', icon: <Clock size={14} /> },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600',   icon: <XCircle size={14} /> },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-600',  icon: <CheckCircle size={14} /> },
};

const BookingCard = ({ booking }) => {
  const isStay = booking.experience_type === 'stay';
  const s = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
  const dateStr = isStay
    ? `${booking.check_in} → ${booking.check_out}`
    : booking.event_date;

  return (
    <motion.div
      data-testid={`customer-booking-${booking.booking_id}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
      style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.05)' }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-navy text-base truncate">{booking.property_name}</h3>
          <p className="font-mono text-gold text-xs font-medium mt-0.5">{booking.booking_id}</p>
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ml-3 shrink-0 ${s.color}`}>
          {s.icon}{s.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 text-xs text-gray-500 mb-4">
        <div className="flex items-center gap-1.5">
          <Calendar size={12} className="text-gold shrink-0" />
          <span className="truncate">{dateStr}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-gold shrink-0" />
          <span>{booking.guests} guest{booking.guests > 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5 justify-end">
          <span className="font-bold text-navy text-sm">₹{fmt(booking.total)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          booking.payment_status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'
        }`}>
          {booking.payment_status === 'paid' ? 'Paid' : booking.payment_status}
        </span>
        <Link to={`/booking/${booking.booking_id}`}
          className="flex items-center gap-1 text-xs font-medium text-navy hover:text-gold transition-colors">
          <Receipt size={12} /> View Receipt <ArrowRight size={11} />
        </Link>
      </div>
    </motion.div>
  );
};

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    bookingsAPI.getCustomer()
      .then(setBookings)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const TABS = [
    { key: 'all', label: 'All Bookings' },
    { key: 'confirmed', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const filtered = activeTab === 'all'
    ? bookings
    : bookings.filter(b => {
        if (activeTab === 'confirmed') return b.status === 'confirmed' || b.status === 'pending';
        return b.status === activeTab;
      });

  const stats = {
    total: bookings.length,
    upcoming: bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length,
    spent: bookings.filter(b => b.payment_status === 'paid').reduce((s, b) => s + (b.total || 0), 0),
  };

  return (
    <div data-testid="customer-dashboard" className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Header */}
        <div className="mb-8">
          <p className="text-gold text-xs uppercase tracking-[0.25em] font-medium mb-2">My Account</p>
          <h1 className="font-serif text-3xl font-black text-navy">My Bookings</h1>
          <p className="text-gray-400 text-sm mt-1">Welcome back, {user?.name}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Bookings', value: stats.total },
            { label: 'Upcoming', value: stats.upcoming },
            { label: 'Total Spent', value: `₹${fmt(stats.spent)}` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 p-5 text-center"
              style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.05)' }}>
              <p className="font-bold text-2xl text-navy">{value}</p>
              <p className="text-xs text-gray-400 mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === t.key ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-gold'
              }`}>
              {t.label}
              {t.key === 'confirmed' && stats.upcoming > 0 && (
                <span className="ml-1.5 bg-gold text-white text-[10px] w-4 h-4 rounded-full inline-flex items-center justify-center">{stats.upcoming}</span>
              )}
            </button>
          ))}
        </div>

        {/* Bookings list */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse border border-gray-100" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Calendar size={40} className="text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium mb-2">
              {activeTab === 'all' ? 'No bookings yet' : `No ${activeTab} bookings`}
            </p>
            <p className="text-gray-400 text-sm mb-5">Discover amazing stays and celebrations across AP & Telangana.</p>
            <Link to="/search" className="bg-navy text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-navy-light transition-colors inline-flex items-center gap-2">
              <Home size={14} /> Browse Properties
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {filtered.map(b => <BookingCard key={b.id} booking={b} />)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CustomerDashboard;
